/**
 * Device data hygiene (QM-D19-003, QM-D02-001 security). The service worker caches only the public shell
 * (`qm-shell-*`). Anything tenant-scoped would live in `qm-data-<partition>` caches; on sign-out and on
 * workspace switch every non-shell cache, all web storage and in-memory query state are dropped, and the
 * service worker is told to purge too, so the next person on the device sees no previous tenant data.
 */
export const SHELL_CACHE_PREFIX = 'qm-shell-';
export const DATA_CACHE_PREFIX = 'qm-data-';
export const DEVICE_BROADCAST_CHANNEL = 'qm-device';

export interface DeviceApis {
  caches?: Pick<CacheStorage, 'keys' | 'delete'> | null;
  localStorage?: Pick<Storage, 'clear'> | null;
  sessionStorage?: Pick<Storage, 'clear'> | null;
  serviceWorker?: { controller: { postMessage(m: unknown): void } | null } | null;
  broadcast?: { postMessage(m: unknown): void } | null;
  /** clear in-memory caches (query cache, store) */
  resetMemory?: () => void;
}

/** Keep only the public shell cache; delete everything else. */
export const isDisposableCache = (name: string) => !name.startsWith(SHELL_CACHE_PREFIX);

export async function clearDeviceData(apis: DeviceApis, reason: 'sign_out' | 'switch_workspace' | 'session_expired'): Promise<string[]> {
  const deleted: string[] = [];
  if (apis.caches) {
    for (const name of await apis.caches.keys()) {
      if (isDisposableCache(name) && (await apis.caches.delete(name))) deleted.push(name);
    }
  }
  try { apis.sessionStorage?.clear(); } catch { /* storage blocked */ }
  try { apis.localStorage?.clear(); } catch { /* storage blocked */ }
  apis.resetMemory?.();
  apis.serviceWorker?.controller?.postMessage({ type: 'qm:purge', reason });
  apis.broadcast?.postMessage({ type: 'qm:purged', reason });
  return deleted;
}

export function browserDeviceApis(resetMemory?: () => void): DeviceApis {
  const g = globalThis as unknown as { caches?: CacheStorage; localStorage?: Storage; sessionStorage?: Storage; navigator?: Navigator; BroadcastChannel?: typeof BroadcastChannel };
  let broadcast: DeviceApis['broadcast'];
  try { broadcast = g.BroadcastChannel ? new g.BroadcastChannel(DEVICE_BROADCAST_CHANNEL) : null; } catch { broadcast = null; }
  return {
    caches: g.caches ?? null,
    localStorage: g.localStorage ?? null,
    sessionStorage: g.sessionStorage ?? null,
    serviceWorker: g.navigator?.serviceWorker ?? null,
    broadcast,
    resetMemory,
  };
}
